import React from 'react';
import {
  TEXTO, SUAVE, BORDA, VERDE, AMBAR, VERMELHO, fmtMoeda, fmtDataHora,
  botaoPrimario, botaoSecundario, botaoPequeno, desabilitado,
} from './comum';

// Etapa 4 — Fechar o mês (ou reabrir, só admin, com justificativa).

const MIN_JUSTIFICATIVA = 10;
const ROTULO_ETAPA = { 1: 'Ir para Extratos', 2: 'Ir para Classificar', 3: 'Ir para Conferir contas' };

function Cartao({ rotulo, valor, cor = TEXTO }) {
  return (
    <div style={{ background: '#f7f5fb', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 10, color: SUAVE, fontWeight: 700, textTransform: 'uppercase' }}>{rotulo}</div>
      <div style={{ fontSize: 15, color: cor, fontWeight: 700, marginTop: 2 }}>{valor}</div>
    </div>
  );
}

export default function EtapaFechar({
  resumo, totalDebitos, totalCreditos, mesFechado, fechamentoAtual, fechamentoAntigo, isAdmin,
  onFechar, onReabrir, onIrEtapa, nomeMes, cardStyle, inputStyle, labelStyle,
}) {
  const [confirmando, setConfirmando] = React.useState(false);
  const [reabrindo, setReabrindo] = React.useState(false);
  const [justificativa, setJustificativa] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => { setConfirmando(false); setReabrindo(false); setJustificativa(''); }, [nomeMes, mesFechado]);

  const fechar = async () => {
    setSalvando(true);
    await onFechar();
    setSalvando(false);
    setConfirmando(false);
  };

  const reabrir = async () => {
    if (justificativa.trim().length < MIN_JUSTIFICATIVA) return;
    setSalvando(true);
    await onReabrir(justificativa.trim());
    setSalvando(false);
  };

  const snap = (fechamentoAtual && fechamentoAtual.snapshot) || fechamentoAntigo || null;
  const fechadoEm = (fechamentoAtual && fechamentoAtual.fechado_em) || (snap && snap.fechadoEm) || null;
  const fechadoPor = snap && snap.fechadoPor && snap.fechadoPor.email;

  if (mesFechado) {
    return (
      <div style={cardStyle}>
        <h4 style={{ color: TEXTO, margin: '0 0 4px 0' }}>{nomeMes} está fechado</h4>
        <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
          Fechado em {fmtDataHora(fechadoEm)}{fechadoPor ? ` por ${fechadoPor}` : ''}
          {!fechamentoAtual && fechamentoAntigo ? ' (pela tela do Balancete, antes do assistente)' : ''}.
          Nenhum lançamento do sistema pode ser incluído, alterado ou excluído neste mês.
        </p>
        {snap && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
            <Cartao rotulo="Contas no fechamento" valor={(snap.linhas || []).length} />
            <Cartao rotulo="Total de débitos" valor={fmtMoeda(snap.totalDebitos)} />
            <Cartao rotulo="Total de créditos" valor={fmtMoeda(snap.totalCreditos)} />
          </div>
        )}

        {!isAdmin ? (
          <div style={{ fontSize: 12, color: SUAVE }}>Somente administradores podem reabrir o período.</div>
        ) : !reabrindo ? (
          <button onClick={() => setReabrindo(true)} style={{ ...botaoSecundario, color: AMBAR, border: `1px solid ${AMBAR}` }}>Reabrir período</button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 560, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)', borderRadius: 8, padding: '10px 12px' }}>
            <label htmlFor="fm-justificativa" style={labelStyle}>Justificativa da reabertura</label>
            <textarea
              id="fm-justificativa"
              value={justificativa}
              onChange={e => setJustificativa(e.target.value)}
              rows={3}
              placeholder="Ex.: extrato do Banco Inter de maio chegou com lançamentos faltantes."
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <span style={{ fontSize: 11, color: SUAVE }}>
              Obrigatória (mínimo de {MIN_JUSTIFICATIVA} caracteres). Fica registrada com a foto do fechamento desfeito. As validações das contas são mantidas; as que mudarem aparecem como alteradas após a validação.
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(() => {
                const off = salvando || justificativa.trim().length < MIN_JUSTIFICATIVA;
                return (
                  <button onClick={reabrir} disabled={off} style={desabilitado({ ...botaoPrimario, background: AMBAR }, off)}>
                    {salvando ? 'Reabrindo…' : `Confirmar reabertura de ${nomeMes}`}
                  </button>
                );
              })()}
              <button onClick={() => { setReabrindo(false); setJustificativa(''); }} disabled={salvando} style={botaoSecundario}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h4 style={{ color: TEXTO, margin: '0 0 4px 0' }}>Fechar {nomeMes}</h4>
      <p style={{ fontSize: 12, color: SUAVE, margin: '0 0 12px 0' }}>
        O fechamento grava a foto do balancete e bloqueia o mês para inclusão, alteração e exclusão de lançamentos.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
        <Cartao rotulo="Total de débitos" valor={fmtMoeda(totalDebitos)} />
        <Cartao rotulo="Total de créditos" valor={fmtMoeda(totalCreditos)} cor={resumo.diferenca > 0 ? VERMELHO : TEXTO} />
        <Cartao rotulo="Contas validadas" valor={`${resumo.validadas}/${resumo.contasTotal}`} cor={resumo.validadas === resumo.contasTotal && resumo.contasTotal > 0 ? VERDE : AMBAR} />
        <Cartao rotulo="A classificar" valor={resumo.aClassificar} cor={resumo.aClassificar ? AMBAR : VERDE} />
      </div>

      {resumo.bloqueios.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: VERMELHO, fontWeight: 700, marginBottom: 6 }}>O que falta para fechar</div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {resumo.bloqueios.map(b => (
              <li key={b.chave} style={{ fontSize: 13, color: TEXTO }}>
                {b.texto}{' '}
                {ROTULO_ETAPA[b.etapa] && (
                  <button onClick={() => onIrEtapa(b.etapa)} style={botaoPequeno}>{ROTULO_ETAPA[b.etapa]}</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {resumo.avisos.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {resumo.avisos.map(a => (
            <div key={a.chave} style={{ fontSize: 12, color: AMBAR, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Aviso: {a.texto} O fechamento não depende disso.</span>
              <button onClick={() => onIrEtapa(a.etapa)} style={botaoPequeno}>{ROTULO_ETAPA[a.etapa]}</button>
            </div>
          ))}
        </div>
      )}

      {!confirmando ? (
        <button onClick={() => setConfirmando(true)} disabled={!resumo.podeFechar} style={desabilitado(botaoPrimario, !resumo.podeFechar)}>
          Fechar o mês
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: 8, padding: '8px 12px' }}>
          <span style={{ fontSize: 13, color: TEXTO }}>Confirmar o fechamento de {nomeMes}?</span>
          <button onClick={fechar} disabled={salvando || !resumo.podeFechar} style={desabilitado({ ...botaoPrimario, background: VERDE }, salvando || !resumo.podeFechar)}>
            {salvando ? 'Fechando…' : 'Confirmar fechamento'}
          </button>
          <button onClick={() => setConfirmando(false)} disabled={salvando} style={botaoSecundario}>Cancelar</button>
        </div>
      )}
    </div>
  );
}
